
import { HttpClient } from '@angular/common/http';
import { Inject, Injectable, DOCUMENT } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { ApiResult, LoginResponse, LoginService, NavDropdownItem, NotificationService, UsuarioAutenticado, UsuarioPermissoes, UsuarioToken } from 'ui-shared';
import { IDENTIDADE_API_URL, OPERACAO_API_URL } from "@classificacao/features/compartilhado/const/constantes";

const contaSuporte = { id: '-1', nome: 'Suporte' } as NavDropdownItem;
const STATUS_CODE_FORBIDDEN = 403;

@Injectable({
    providedIn: 'root'
})
export class ContaEmUsoService {

    private contas: NavDropdownItem[] = [];
    private selecionada: NavDropdownItem;

    constructor(
        @Inject(IDENTIDADE_API_URL) private identidadeUrl: string,
        @Inject(OPERACAO_API_URL) private operacaoUrl: string,
        @Inject(HttpClient) private httpClient: HttpClient,
        private loginService: LoginService,
        private notificationService: NotificationService,
        @Inject(DOCUMENT) private document: Document
    ) { }

    obterItens(permicaoAmbiente: string, modulo: string): Observable<{ itens: NavDropdownItem[]; selecionada: NavDropdownItem | null }> {
        return this.obter(permicaoAmbiente, modulo).pipe(
            switchMap(({ data }) =>
                this.loginService.token$.pipe(
                    map((token: UsuarioToken | null) => {
                        let selecionada: NavDropdownItem | null = null;
                        if (token) {
                            const { suporte, contaEmUso } = token;
                            if (contaEmUso?.id) {
                                selecionada = (data!.find(x => x.id === contaEmUso.id) ?? null) as NavDropdownItem;
                            } else if (suporte) {
                                selecionada = contaSuporte as NavDropdownItem;
                            } else if (data!.length === 1) {
                                this.selecionar(data![0], modulo).subscribe();
                            }
                        }
                        this.contas = token?.suporte ? [contaSuporte, ...data!] : data!;
                        const itens: NavDropdownItem[] = this.contas.map(conta => ({
                            id: conta.id,
                            nome: conta.nome,
                            rota: conta.rota,
                            acao: () => this.selecionar(conta, modulo).subscribe()
                        }));
                        return { itens, selecionada };
                    })
                )
            )
        );
    }

    selecionar(conta: NavDropdownItem, modulo: string): Observable<any> {
        if (conta.rota) {
            this.selecionada = conta;
            this.document.location.href = this.document.location.origin + conta.rota;
            return of(conta);
        }

        let usuarioAutenticado: UsuarioAutenticado;
        const contaSelecionadaAnterior = this.selecionada;
        const accessTokenAnterior = this.loginService.accessToken;

        return this.atualizarToken(conta.id, modulo).pipe(
            map(result => {
                this.loginService.salvarToken(result.data!.accessToken);
                this.loginService.salvarUsuario(result.data!.usuarioAutenticado);
                usuarioAutenticado = result.data!.usuarioAutenticado;
                this.selecionada = conta;
            }),
            switchMap(() => {
                if (conta.id != contaSuporte.id) {
                    return this.obterDetalhes().pipe(
                        map(result => {
                            // controlePorProjeto vem junto das permissões e fica guardado com elas (Rules.controlePorProjeto).
                            let usuario: UsuarioPermissoes & { controlePorProjeto?: boolean } = result.data;

                            usuario.administrador = usuarioAutenticado.claims.find(claim => claim.type == "administrador")?.value == "True";
                            usuario.contaId = conta.id;

                            if (usuario.multiempresa) {
                                usuario.perfis.push({
                                    id: usuario.id,
                                    usuarioId: usuario.id,
                                    perfilAcesso: 'Multiempresa',
                                    permiteEdicao: true
                                });
                            }
                            if (usuario.habilitarSugestaoPreenchimentoCriativa) {
                                usuario.perfis.push({
                                    id: usuario.id,
                                    usuarioId: usuario.id,
                                    perfilAcesso: 'HabilitarSugestaoPreenchimentoCriativa',
                                    permiteEdicao: true
                                });
                            }
                            if (usuario.habilitarSugestaoPreenchimento) {
                                usuario.perfis.push({
                                    id: usuario.id,
                                    usuarioId: usuario.id,
                                    perfilAcesso: 'HabilitarSugestaoPreenchimento',
                                    permiteEdicao: true
                                });
                            }
                            if (usuario.habilitarClassificacaoFiscal) {
                                usuario.perfis.push({
                                    id: usuario.id,
                                    usuarioId: usuario.id,
                                    perfilAcesso: 'HabilitarClassificacaoFiscal',
                                    permiteEdicao: true
                                });
                            }
                            this.loginService.salvarPermissoes(usuario);
                        }),
                        catchError(err => {
                            switch (err.status) {
                                case STATUS_CODE_FORBIDDEN:
                                    if (this.contas.length > 1){
                                        this.selecionada = contaSelecionadaAnterior;
                                        this.loginService.salvarToken(accessTokenAnterior);
                                        this.loginService.salvarUsuario(usuarioAutenticado);
                                        this.notificationService.openError(`Conta ${conta.nome} não está habilitado para acesso ao módulo solicitado.`)
                                        break;
                                    }
                                    this.document.location.href = this.document.location.origin + '/acesso-negado';
                                default:
                                    return of(err.status)
                            }
                        })
                    )
                }
                return of(conta);
            }),
            switchMap(() => {
                if (conta.id != contaSuporte.id) {
                    return this.atualizarAcessoUsuario().pipe(
                        catchError(err => of(err.status)),
                    )
                }
                return of(conta);
            }),
            switchMap(() => this.document.location.href = this.document.location.origin + '/login')
        );
    }

    private obter(permissaoAmbiente: string, modulo: string) {
        const apiUrl = `${this.identidadeUrl}/v1/contas?permissaoAmbiente=${permissaoAmbiente}&modulo=${modulo}`;
        return this.httpClient.get<ApiResult<NavDropdownItem[]>>(apiUrl);
    }

    private atualizarToken(contaId: any, modulo: string) {
        const apiUrl = `${this.identidadeUrl}/v1/autenticacao/trocarConta${contaId !== '-1' ? `?contaId=${contaId}&modulo=${modulo}` : `?modulo=${modulo}`}`;
        return this.httpClient.get<ApiResult<LoginResponse>>(apiUrl);
    }

    private obterDetalhes() {
        const apiUrl = `${this.operacaoUrl}/v1/usuario/detalhes`;
        return this.httpClient.get<ApiResult>(apiUrl);
    }

    private atualizarAcessoUsuario() {
        const apiUrl = `${this.operacaoUrl}/v1/usuario/ultimoacesso`;
        return this.httpClient.put<ApiResult>(apiUrl, {});
    }
}
